import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Plus, Tag } from 'lucide-react';

// Component for displaying a single tag
export const WineTag = ({ tag, onRemove, onClick }) => {
  const getCount = () => {
    return tag.count > 1 ? `(${tag.count})` : '';
  };
  
  return (
    <span 
      className={`badge bg-${tag.color || 'secondary'} me-1 mb-1`}
      style={{ cursor: onClick ? 'pointer' : 'default' }}
      onClick={onClick ? () => onClick(tag) : undefined}
    >
      {tag.name}{getCount()}
      {onRemove && (
        <button 
          className="btn-close btn-close-white ms-1" 
          style={{ fontSize: '0.5rem' }}
          onClick={(e) => {
            e.stopPropagation();
            onRemove(tag);
          }}
        ></button>
      )}
    </span>
  );
};

// Component for managing tags
export const TagManager = ({ wineID, refreshTags }) => {
  const [tags, setTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [newTag, setNewTag] = useState({
    name: '',
    color: 'secondary',
    category: 'Process',
    description: ''
  });
  
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch all available tags
        const tagsRes = await axios.get('/api/wine-tags');
        setAvailableTags(tagsRes.data);
        
        // Fetch tags for this wine
        if (wineID) {
          const wineTagsRes = await axios.get(`/api/wines/${wineID}/tags`);
          setTags(wineTagsRes.data);
        }
      } catch (error) {
        console.error('Error fetching tags:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [wineID, refreshTags]);
  
  const handleAddTag = async (tagID) => {
    try {
      await axios.post(`/api/wines/${wineID}/tags`, { tagID });
      // Refresh tags
      const wineTagsRes = await axios.get(`/api/wines/${wineID}/tags`);
      setTags(wineTagsRes.data);
    } catch (error) {
      console.error('Error adding tag:', error);
    }
  };
  
  const handleRemoveTag = async (tag) => {
    if (window.confirm(`Remove tag "${tag.name}" from this wine?`)) {
      try {
        await axios.delete(`/api/wines/${wineID}/tags/${tag.tagID}`);
        setTags(tags.filter(t => t.tagID !== tag.tagID));
      } catch (error) {
        console.error('Error removing tag:', error);
      }
    }
  };
  
  const handleCreateTag = async () => {
    try {
      const res = await axios.post('/api/wine-tags', newTag);
      setAvailableTags([...availableTags, res.data]);
      setShowTagModal(false);
      setNewTag({
        name: '',
        color: 'secondary',
        category: 'Process',
        description: ''
      });
      
      // Auto-add the new tag to the wine
      await handleAddTag(res.data.tagID);
    } catch (error) {
      console.error('Error creating tag:', error);
    }
  };
  
  // Filter out tags already assigned to the wine
  const filteredAvailableTags = availableTags.filter(
    aTag => !tags.some(wTag => wTag.tagID === aTag.tagID)
  );
  
  return (
    <div className="wine-tags-manager">
      {/* Display existing tags */}
      <div className="mb-2">
        {tags.length > 0 ? (
          <div className="d-flex flex-wrap">
            {tags.map(tag => (
              <WineTag 
                key={tag.tagID} 
                tag={tag} 
                onRemove={handleRemoveTag} 
              />
            ))}
          </div>
        ) : (
          <div className="text-muted small">No tags assigned</div>
        )}
      </div>
      
      {/* Tag dropdown */}
      <div className="dropdown">
        <button 
          className="btn btn-sm btn-outline-secondary dropdown-toggle" 
          type="button" 
          id="addTagDropdown" 
          data-bs-toggle="dropdown" 
          aria-expanded="false"
        >
          <Tag size={16} className="me-1" />
          Add Tag
        </button>
        <ul className="dropdown-menu" aria-labelledby="addTagDropdown">
          {filteredAvailableTags.length > 0 ? (
            filteredAvailableTags.map(tag => (
              <li key={tag.tagID}>
                <button 
                  className="dropdown-item" 
                  onClick={() => handleAddTag(tag.tagID)}
                >
                  <span className={`badge bg-${tag.color} me-2`}>&nbsp;</span>
                  {tag.name}
                </button>
              </li>
            ))
          ) : (
            <li><span className="dropdown-item text-muted">No more tags available</span></li>
          )}
          <li><hr className="dropdown-divider" /></li>
          <li>
            <button 
              className="dropdown-item" 
              onClick={() => setShowTagModal(true)}
            >
              <Plus size={16} className="me-1" />
              Create New Tag...
            </button>
          </li>
        </ul>
      </div>
      
      {/* Create Tag Modal */}
      {showTagModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div className="modal show" style={{ display: 'block' }} tabIndex="-1">
            <div className="modal-dialog">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Create New Tag</h5>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setShowTagModal(false)}
                  ></button>
                </div>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label">Tag Name</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={newTag.name}
                      onChange={(e) => setNewTag({...newTag, name: e.target.value})}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Color</label>
                    <select 
                      className="form-select"
                      value={newTag.color}
                      onChange={(e) => setNewTag({...newTag, color: e.target.value})}
                    >
                      <option value="primary">Blue</option>
                      <option value="secondary">Gray</option>
                      <option value="success">Green</option>
                      <option value="danger">Red</option>
                      <option value="warning">Yellow</option>
                      <option value="info">Light Blue</option>
                      <option value="dark">Black</option>
                    </select>
                    <div className="mt-2">
                      <span className={`badge bg-${newTag.color}`}>Preview</span>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Category</label>
                    <select 
                      className="form-select"
                      value={newTag.category}
                      onChange={(e) => setNewTag({...newTag, category: e.target.value})}
                    >
                      <option value="Process">Process</option>
                      <option value="Quality">Quality</option>
                      <option value="Maintenance">Maintenance</option>
                      <option value="Status">Status</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea 
                      className="form-control" 
                      rows="2"
                      value={newTag.description}
                      onChange={(e) => setNewTag({...newTag, description: e.target.value})}
                    ></textarea>
                  </div>
                </div>
                <div className="modal-footer">
                  <button 
                    type="button" 
                    className="btn btn-secondary" 
                    onClick={() => setShowTagModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-primary"
                    onClick={handleCreateTag}
                    disabled={!newTag.name}
                  >
                    Create Tag
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default { WineTag, TagManager };